import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import {
  Api,
  canDeleteContent,
  canManageContentActivation,
  CHAT_LESSON_TOPIC_OPTIONS,
  hasPermission,
  IJourney,
  IJourneyListResponse,
  PERMISSIONS,
} from "@model";
import { SearchBar, ISearchQuery } from "@components";
import { useIsCompact } from "../../hooks/useIsCompact";
import {
  Button,
  Checkbox,
  Empty,
  Pagination,
  Select,
  Spin,
  Tag,
  message,
  Modal,
  Tooltip,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { CHAT_LESSON_LANGUAGES } from "../ChatLesson/chatLessonLanguages";
import JourneyLogsModal from "./JourneyLogsModal";

const JourneyStat = ({
  label,
  total,
  today,
  accent,
}: {
  label: string;
  total: number;
  today: number;
  accent: string;
}) => (
  <div
    style={{
      background: "#f6f8fa",
      borderRadius: 8,
      padding: "8px 12px",
      borderLeft: `3px solid ${accent}`,
      minWidth: 0,
    }}
  >
    <div style={{ fontSize: 12, color: "#8c8c8c" }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{total}</div>
    <div style={{ fontSize: 12, color: accent }}>{today} today</div>
  </div>
);

const JourneyListPage = () => {
  const isCompact = useIsCompact();
  const [searchParams, setSearchParams] = useSearchParams();
  const [journeys, setJourneys] = useState<IJourney[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState<ISearchQuery>({
    q: searchParams.get("q") ?? "",
  });
  const [languageFilter, setLanguageFilter] = useState<string | undefined>();
  const [topicFilter, setTopicFilter] = useState<string | undefined>();
  const [logsJourneyId, setLogsJourneyId] = useState<string | null>(null);

  const navigate = useNavigate();
  const canCreate = hasPermission(PERMISSIONS.journeysCreate);
  const canEdit = hasPermission(PERMISSIONS.journeysEdit);
  const canActivate = canManageContentActivation();
  const canViewLogs = hasPermission(PERMISSIONS.journeysLogs);

  const syncStateFromUrl = useCallback(() => {
    const q = searchParams.get("q") ?? "";
    setSearchQuery((prev) => (prev.q !== q ? { q } : prev));
    setCurrentPage(Math.max(1, Number(searchParams.get("page")) || 1));
    const ps = Number(searchParams.get("pageSize"));
    if (ps) setPageSize(Math.min(100, Math.max(10, ps)));
    const lang = searchParams.get("language");
    setLanguageFilter(lang || undefined);
    const topic = searchParams.get("topic");
    setTopicFilter(topic || undefined);
  }, [searchParams]);

  useEffect(() => {
    syncStateFromUrl();
  }, [syncStateFromUrl]);

  const fetchJourneys = useCallback(() => {
    setLoading(true);
    Api.get(`/api/admin/panel/journeys`, {
      params: {
        language: languageFilter,
        topic: topicFilter,
        page: currentPage,
        limit: pageSize,
        timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
      },
    })
      .then((response) => {
        const result = response.data.data as IJourneyListResponse;
        let items = result?.data ?? [];
        if (searchQuery.q.trim()) {
          const q = searchQuery.q.trim().toLowerCase();
          items = items.filter((j) => {
            if (j.title?.toLowerCase().includes(q)) return true;
            const metadata = j.metadata ?? {};
            return Object.values(metadata).some(
              (entry) =>
                entry?.title?.toLowerCase().includes(q) ||
                entry?.description?.toLowerCase().includes(q),
            );
          });
        }
        setJourneys(items);
        setTotalCount(result?.total ?? items.length);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        message.error("Failed to fetch journeys");
        setLoading(false);
      });
  }, [languageFilter, topicFilter, currentPage, pageSize, searchQuery.q]);

  useEffect(() => {
    fetchJourneys();
  }, [fetchJourneys]);

  const updateUrl = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
    updateUrl({
      page: String(page),
      ...(newPageSize ? { pageSize: String(newPageSize) } : {}),
    });
  };

  const handleToggleActive = useCallback(
    async (record: IJourney) => {
      try {
        await Api.patch(`/api/admin/panel/journeys/${record._id}/activation`, {
          isActive: !record.isActive,
        });
        message.success(
          `Journey ${!record.isActive ? "activated" : "deactivated"} successfully`,
        );
        fetchJourneys();
      } catch {
        message.error("Failed to update journey status");
      }
    },
    [fetchJourneys],
  );

  const handleDelete = useCallback(
    (record: IJourney) => {
      Modal.confirm({
        title: "Delete Journey",
        content: `Are you sure you want to delete "${record.title}"?`,
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await Api.delete(`/api/admin/panel/journeys/${record._id}`);
            message.success("Journey deleted successfully");
            fetchJourneys();
          } catch {
            message.error("Failed to delete journey");
          }
        },
      });
    },
    [fetchJourneys],
  );

  const goToEdit = useCallback(
    (record: IJourney) => {
      const returnTo = searchParams.toString()
        ? `/panel/journeys?${searchParams.toString()}`
        : "/panel/journeys";
      navigate(`/panel/journeys/create/${record._id}`, {
        state: { ...record, returnTo },
      });
    },
    [navigate, searchParams],
  );

  const topicLabel = (topic?: string) =>
    CHAT_LESSON_TOPIC_OPTIONS.find((o) => o.value === topic)?.label ?? topic;

  const topicLabels = (record: IJourney) =>
    (Array.isArray(record.topic) ? record.topic : [record.topic])
      .map((topic) => topicLabel(topic))
      .filter((topic): topic is string => !!topic);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isCompact ? "stretch" : "center",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: isCompact ? "stretch" : "center",
            flexDirection: isCompact ? "column" : "row",
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          <SearchBar
            style={{ flex: "1", width: isCompact ? "100%" : undefined, minWidth: isCompact ? 0 : 200, margin: 0 }}
            value={searchQuery.q}
            onChange={(e) => {
              setSearchQuery(e);
              setCurrentPage(1);
              setLoading(true);
              updateUrl({ q: e.q || undefined, page: "1" });
            }}
            searchType="Enter"
          />
          <Select
            placeholder="Language"
            allowClear
            style={{ width: isCompact ? "100%" : 140 }}
            value={languageFilter ?? undefined}
            options={CHAT_LESSON_LANGUAGES.map((lang) => ({
              value: lang.key,
              label: lang.label,
            }))}
            onChange={(value) => {
              setLanguageFilter(value ?? undefined);
              setCurrentPage(1);
              updateUrl({ language: value ?? undefined, page: "1" });
            }}
          />
          <Select
            placeholder="Topic"
            allowClear
            style={{ width: isCompact ? "100%" : 180 }}
            value={topicFilter ?? undefined}
            options={CHAT_LESSON_TOPIC_OPTIONS}
            onChange={(value) => {
              setTopicFilter(value ?? undefined);
              setCurrentPage(1);
              updateUrl({ topic: value ?? undefined, page: "1" });
            }}
          />
        </div>
        {canCreate && <Button
          type="primary"
          icon={<PlusOutlined />}
          style={isCompact ? { width: "100%" } : undefined}
          onClick={() => {
            const returnTo = searchParams.toString()
              ? `/panel/journeys?${searchParams.toString()}`
              : "/panel/journeys";
            navigate(`/panel/journeys/create`, { state: { returnTo } });
          }}
        >
          New
        </Button>}
      </div>
      <Spin spinning={loading}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {!loading && journeys.length === 0 && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No journeys" />
          )}
          {journeys.map((record) => {
            const statistics = record.statistics;
            const topics = topicLabels(record);
            const stepCount = record.steps?.length ?? 0;
            return (
              <div
                key={record._id ?? record.title}
                className={record.isDeleted === true ? "deleted-record" : undefined}
                style={{
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: "12px 16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: isCompact ? 8 : 16,
                    flexWrap: "wrap",
                    flexDirection: isCompact ? "column" : "row",
                  }}
                >
                  {!isCompact && (
                  <div style={{ display: "flex", gap: 12, paddingTop: 2 }}>
                    <Checkbox
                      checked={record.isActive === true}
                      disabled={!canActivate}
                      onChange={() => handleToggleActive(record)}
                    >
                      Active
                    </Checkbox>
                    <Checkbox checked={record.isPremium === true} disabled>
                      Premium
                    </Checkbox>
                  </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0, width: isCompact ? "100%" : undefined }}>
                    {canEdit ? (
                      <a
                        href={`/panel/journeys/create/${record._id ?? ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          goToEdit(record);
                        }}
                        style={{
                          fontWeight: 600,
                          color: "#1890ff",
                          textDecoration: "underline",
                          wordBreak: "break-word",
                        }}
                      >
                        {record.title}
                      </a>
                    ) : (
                      <span style={{ fontWeight: 600 }}>{record.title}</span>
                    )}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                        alignItems: "center",
                        marginTop: 6,
                      }}
                    >
                      {topics.map((topic) => (
                        <Tag key={topic} style={{ margin: 0 }}>
                          {topic}
                        </Tag>
                      ))}
                      <Tag style={{ margin: 0 }}>{stepCount} steps</Tag>
                      {record.creator?.name && (
                        <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                          {record.creator.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {canEdit && (
                      <Tooltip title="Edit">
                        <Button
                          type="link"
                          aria-label="Edit"
                          icon={<EditOutlined />}
                          onClick={() => goToEdit(record)}
                        />
                      </Tooltip>
                    )}
                    {canViewLogs && (
                      <Tooltip title="Logs">
                        <Button
                          type="link"
                          aria-label="Logs"
                          icon={<FileSearchOutlined />}
                          disabled={!record._id}
                          onClick={() => setLogsJourneyId(record._id ?? null)}
                        />
                      </Tooltip>
                    )}
                    {canDeleteContent(
                      record.creator,
                      PERMISSIONS.journeysDelete,
                      PERMISSIONS.journeysDeleteOwn,
                    ) && (
                      <Tooltip title="Delete">
                        <Button
                          type="link"
                          danger
                          aria-label="Delete"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(record)}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
                {isCompact && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
                    <Checkbox
                      checked={record.isActive === true}
                      disabled={!canActivate}
                      onChange={() => handleToggleActive(record)}
                    >
                      Active
                    </Checkbox>
                    <Checkbox checked={record.isPremium === true} disabled>
                      Premium
                    </Checkbox>
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact
                      ? "1fr 1fr"
                      : "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: 8,
                    marginTop: 12,
                  }}
                >
                  <JourneyStat
                    label="Tried"
                    total={statistics?.usersCount ?? 0}
                    today={statistics?.todayUsersCount ?? 0}
                    accent="#1890ff"
                  />
                  <JourneyStat
                    label="Completed"
                    total={statistics?.completedUsersCount ?? 0}
                    today={statistics?.todayCompletedUsersCount ?? 0}
                    accent="#52c41a"
                  />
                  <JourneyStat
                    label="In progress"
                    total={statistics?.inProgressUsersCount ?? 0}
                    today={statistics?.todayInProgressUsersCount ?? 0}
                    accent="#fa8c16"
                  />
                  <JourneyStat
                    label="Left"
                    total={statistics?.leftUsersCount ?? 0}
                    today={statistics?.todayLeftUsersCount ?? 0}
                    accent="#ff4d4f"
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 16, textAlign: isCompact ? "center" : "right" }}>
          <Pagination
            size="small"
            simple={isCompact}
            current={currentPage}
            pageSize={pageSize}
            total={totalCount || currentPage * pageSize}
            showSizeChanger={!isCompact}
            pageSizeOptions={["10", "20", "50", "100"]}
            onChange={handlePaginationChange}
          />
        </div>
      </Spin>
      {logsJourneyId && (
        <JourneyLogsModal
          journeyId={logsJourneyId}
          open={!!logsJourneyId}
          onClose={() => setLogsJourneyId(null)}
        />
      )}
    </div>
  );
};

export default JourneyListPage;
